import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { css } from '@emotion/react'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'
import ButtonList from '@groovehq/internal-design-system/lib/components/ButtonList/ButtonList'
import { AdminAccessDrawer } from 'subapps/settings/components/drawers/NoAccess'
import { useDrawer } from 'ducks/drawers/hooks'
import { buildDrawerQueryParam, getCurrentDrawerId } from 'ducks/drawers/util'
import { doUpdateAccount } from 'actions/account'
import { doOpenMainPage } from 'actions/pages'
import { selectQueryParams } from 'selectors/location'
import { selectShowDemoMailboxLink } from 'selectors/trial_setup'
import { CHANNEL_EMAIL_TYPES, drawerConfig } from '../Channels.data'

const { DEFAULT_DRAWER_TITLE } = drawerConfig

const demoLinkStyle = theme => css`
  all: unset;
  cursor: pointer;
  color: ${theme.color.primary.info};
`

const SelectEmailChannel = ({
  onClose,
  onExit,
  previousDrawer,
  drawerSource: source = 'settings',
}) => {
  const dispatch = useDispatch()
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const queryParams = useSelector(selectQueryParams)
  // get the current drawer id from the url string in browser address bar
  const currentDrawerId = getCurrentDrawerId(queryParams)
  const showDemoLink = useSelector(selectShowDemoMailboxLink)
  const [loadingDemo, setLoadingDemo] = useState(false)

  const { drawerId: nextDrawerId, openDrawer: openNextDrawer } = useDrawer(
    CHANNEL_EMAIL_TYPES[selectedChannel]?.drawer
  )

  useEffect(
    () => {
      if (isDrawerOpen && selectedChannel && currentDrawerId !== nextDrawerId) {
        // prevent drawer from being triggered to open again if the drawer is closed
        // i.e. closed being, the previous state's drawer is missing/changed from address bar query
        setSelectedChannel(null)
        setIsDrawerOpen(false)
      } else if (selectedChannel && currentDrawerId !== nextDrawerId) {
        // now useDrawer is initialised with value from currentDrawer
        // invoke openDrawer
        openNextDrawer('new', {
          query: {
            ...buildDrawerQueryParam(
              nextDrawerId,
              'drawerChannelType',
              selectedChannel
            ),
          },
          additionalProps: {
            drawerSource: source,
          },
        })
        setIsDrawerOpen(true)
      }
    },
    [
      currentDrawerId,
      nextDrawerId,
      selectedChannel,
      openNextDrawer,
      isDrawerOpen,
      source,
    ]
  )

  const channelList = useMemo(
    () => {
      return Object.keys(CHANNEL_EMAIL_TYPES).map(type => {
        const channel = {
          type,
          ...CHANNEL_EMAIL_TYPES[type],
          ...(loadingDemo && { disabled: true }),
        }
        return channel
      })
    },
    [loadingDemo]
  )

  const handleOpenNextDrawer = useCallback(
    type => {
      setSelectedChannel(type)
    },
    [setSelectedChannel]
  )

  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: false })
    },
    [onClose]
  )

  const handleDemoInbox = useCallback(
    async () => {
      setLoadingDemo(true)
      await dispatch(
        doUpdateAccount({ show_demo_mailbox: true }, { moduleOptions: {} })
      )
      onClose({ ignoreStack: true })
      dispatch(doOpenMainPage())
    },
    [dispatch, onClose, setLoadingDemo]
  )

  const footer = (
    <ModalBtns
      tertiaryBtnText={previousDrawer ? 'Back' : 'Close'}
      onClickTertiaryBtn={handleBack}
    />
  )

  return (
    <AdminAccessDrawer
      title={DEFAULT_DRAWER_TITLE}
      footer={footer}
      onClose={onExit}
      data-test-id="drawer-select-email-channel"
    >
      <HeaderWithImage
        className="grui mt-14 mb-12"
        intro="Select the email provider below."
      />
      <ButtonList
        list={channelList}
        onSelect={handleOpenNextDrawer}
        size="big"
      />
      {showDemoLink && (
        <p className="grui mt-10 text-right">
          Not ready to create your own yet?<br />
          <button css={demoLinkStyle} onClick={handleDemoInbox}>
            Check out a demo {app.t('mailbox')}
          </button>
        </p>
      )}
    </AdminAccessDrawer>
  )
}

export default SelectEmailChannel
